import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

export default function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [searchClient, setSearchClient] = useState("");
  const [items, setItems] = useState([{ product_id: "", hsn: "", qty: 1, rate: 0, tax_rate: 18 }]);
  
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");

  useEffect(() => {
    Promise.all([
      fetch("/api/customers").then(r=>r.json()),
      fetch("/api/products").then(r=>r.json()),
      fetch("/api/company").then(r=>r.json()),
      fetch("/api/invoices").then(r=>r.json())
    ]).then(([custData, prodData, compData, invData]) => {
      setCustomers(Array.isArray(custData)?custData:[]);
      setProducts(Array.isArray(prodData)?prodData:[]);
      setCompany(compData||{});
      const invList = Array.isArray(invData)?invData:[];
      
      if(id) {
         const inv = invList.find(i => i.id == id);
         if(inv) {
            setInvoiceNo(inv.invoice_no);
            setSelectedCustomer(inv.customer_id);
            setItems(inv.items);
            if(inv.paymentTerms) setPaymentTerms(inv.paymentTerms);
            setInvoiceDate(inv.date || new Date().toISOString().split('T')[0]);
         }
      } else {
         let maxNum = 1127;
         invList.forEach(inv => {
            const match = String(inv.invoice_no).match(/\d+/);
            if(match) {
               const val = parseInt(match[0], 10);
               if(val > maxNum) maxNum = val;
            }
         });
         const nextNum = String(maxNum + 1).padStart(4, '0');
         setInvoiceNo(`${nextNum}`);
         setInvoiceDate(new Date().toISOString().split('T')[0]); // Aaj ki date by default
      }
    });
  }, [id]);

  const handleItem = (index, field, val) => {
    const newItems = [...items];
    newItems[index][field] = val;
    if (field === "product_id") {
      const prod = products.find(p => p.id == val);
      if (prod) {
        newItems[index].rate = prod.price || 0;
        newItems[index].hsn = prod.hsn || "";
        newItems[index].tax_rate = prod.tax_rate || 18;
      }
    }
    setItems(newItems);
  };

  const customer = customers.find(c => c.id == selectedCustomer) || {};
  const isSameState = (company.state_code || "08") === (customer.state_code || "08");
  
  let totalBase = 0, totalCGST = 0, totalSGST = 0, totalIGST = 0;
  items.forEach(item => {
    const base = item.qty * item.rate;
    totalBase += base;
    const taxAmt = base * (item.tax_rate / 100);
    if (isSameState) { totalCGST += taxAmt / 2; totalSGST += taxAmt / 2; } else { totalIGST += taxAmt; }
  });
  const grandTotal = Math.round(totalBase + totalCGST + totalSGST + totalIGST);

  const handleSave = async () => {
    if (!selectedCustomer) return alert("Please select a Billed To (Customer)!");
    if (!invoiceDate) return alert("Please select an Invoice Date!");

    const payload = { 
       invoice_no: invoiceNo, 
       customer_id: selectedCustomer, 
       items, 
       total: grandTotal, 
       date: invoiceDate,
       paymentTerms 
    };

    const url = id ? `/api/invoices/${id}` : "/api/invoices";
    const method = id ? "PUT" : "POST";

    await fetch(url, { method, headers: {"Content-Type":"application/json"}, body: JSON.stringify(payload) });
    alert(id ? "Invoice Updated!" : "Invoice Saved!");
    navigate("/invoices");
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-4">
         <h1 className="text-2xl font-bold">{id ? "Edit Tax Invoice" : "New Tax Invoice"}</h1>
         <button onClick={()=>navigate("/invoices")} className="text-gray-500 font-bold hover:text-red-600">✕ Cancel</button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 p-4 border rounded">
            <label className="block text-sm font-bold mb-2">Billed To:</label>
            <input type="text" placeholder="🔍 Search Client Name or GST..." className="w-full p-2 border rounded mb-2 border-blue-400 font-bold text-sm bg-blue-50 focus:bg-white transition" value={searchClient} onChange={(e)=>setSearchClient(e.target.value)} />
            <select className="w-full p-2 border rounded" value={selectedCustomer} onChange={(e)=>setSelectedCustomer(e.target.value)}>
              <option value="">-- Select Client --</option>
              {customers.filter(c => (c.name || "").toLowerCase().includes(searchClient.toLowerCase()) || (c.gstin || "").toLowerCase().includes(searchClient.toLowerCase())).map(c => <option key={c.id} value={c.id}>{c.name} (GST: {c.gstin || "N/A"})</option>)}
            </select>
            {customer.name && <div className="mt-2 text-sm text-gray-600">State Code: {customer.state_code} | Place of Supply: {isSameState ? "Intra-State (CGST+SGST)" : "Inter-State (IGST)"}</div>}
          </div>
          <div className="bg-gray-50 p-4 border rounded">
             <label className="block text-sm font-bold mb-2">Invoice No & Date:</label>
             <div className="flex gap-2 mb-2">
                 <input type="text" title="Invoice Number" className="w-1/2 p-2 border rounded font-bold text-gray-800" value={invoiceNo} onChange={(e)=>setInvoiceNo(e.target.value)} />
                 <input type="date" title="Invoice Date" className="w-1/2 p-2 border rounded font-bold text-gray-800 cursor-pointer" value={invoiceDate} onChange={(e)=>setInvoiceDate(e.target.value)} />
             </div>
             <textarea placeholder="Mode/Terms of Payment" className="w-full p-2 border rounded text-sm h-12" value={paymentTerms} onChange={(e)=>setPaymentTerms(e.target.value)}></textarea>
          </div>
      </div>

      <table className="w-full mb-4 border">
        <thead className="bg-gray-100 border-b"><tr className="text-left text-sm">
          <th className="p-2">Service / Particulars</th><th className="p-2">HSN/SAC</th><th className="p-2">Qty</th><th className="p-2">Rate (₹)</th><th className="p-2">GST %</th><th className="p-2">Amount (₹)</th>
        </tr></thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="border-b">
              <td className="p-2">
                <select className="w-full p-1 border" value={item.product_id} onChange={e=>handleItem(i, "product_id", e.target.value)}>
                  <option value="">- Select -</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </td>
              <td className="p-2">{item.hsn}</td>
              <td className="p-2"><input type="number" className="w-16 p-1 border" value={item.qty} onChange={e=>handleItem(i, "qty", e.target.value)} /></td>
              <td className="p-2"><input type="number" className="w-24 p-1 border" value={item.rate} onChange={e=>handleItem(i, "rate", e.target.value)} /></td>
              <td className="p-2"><select className="w-16 p-1 border font-bold text-gray-700" value={item.tax_rate !== undefined ? item.tax_rate : 18} onChange={e=>handleItem(i, "tax_rate", e.target.value)}><option value="18">18%</option><option value="12">12%</option><option value="5">5%</option><option value="0">0%</option></select></td>
              <td className="p-2 font-bold">{item.qty * item.rate}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={() => setItems([...items, {product_id:"", hsn:"", qty:1, rate:0, tax_rate:18}])} className="bg-gray-200 hover:bg-gray-300 px-4 py-1 rounded text-sm mb-6">+ Add Item</button>

      <div className="flex justify-end">
        <div className="w-1/3 border p-4 rounded bg-gray-50 text-sm">
          <div className="flex justify-between mb-1"><span>Taxable Value:</span><span>₹{totalBase.toFixed(2)}</span></div>
          {isSameState ? (
            <><div className="flex justify-between mb-1"><span>CGST:</span><span>₹{totalCGST.toFixed(2)}</span></div><div className="flex justify-between mb-1 border-b pb-1"><span>SGST:</span><span>₹{totalSGST.toFixed(2)}</span></div></>
          ) : (
            <div className="flex justify-between mb-1 border-b pb-1"><span>IGST:</span><span>₹{totalIGST.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between font-bold text-lg mt-2"><span>Total:</span><span>₹{grandTotal.toFixed(2)}</span></div>
        </div>
      </div>
      <div className="mt-6">
         <button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded shadow w-full text-lg cursor-pointer transition">
            {id ? "Update Invoice" : "Save Invoice"}
         </button>
      </div>
    </div>
  );
}
